// src/lib/firebaseUtils.ts
import type { Timestamp } from 'firebase/firestore';
import type { Character, ChatMessage } from './types'; // Added ChatMessage
import { getStableGreetingId } from './types'; // Added getStableGreetingId

/**
 * Converts Firestore Timestamps or JavaScript Date objects to milliseconds since epoch.
 * If input is already a number (milliseconds), it's returned directly.
 * Returns 0 for null/undefined or unparseable inputs.
 */
export const convertTimestampToMillis = (timestamp: any): number => {
  if (!timestamp) {
    // console.warn("convertTimestampToMillis: Received null or undefined timestamp.");
    return 0;
  }
  if (timestamp.toMillis && typeof timestamp.toMillis === 'function') {
    // Firestore Timestamp instance
    return timestamp.toMillis();
  }
  if (typeof timestamp === 'object' && timestamp !== null && 
      'seconds' in timestamp && typeof timestamp.seconds === 'number' && 
      'nanoseconds' in timestamp && typeof timestamp.nanoseconds === 'number') {
    // Plain object that looks like a Firestore Timestamp (e.g., from JSON serialization or direct data)
    // This can happen if a serverTimestamp() has resolved but hasn't been hydrated into a Timestamp instance by the SDK yet.
    return timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1000000);
  }
  if (timestamp instanceof Date) {
    // JavaScript Date object
    return timestamp.getTime();
  }
  if (typeof timestamp === 'number') {
    // Already a number (milliseconds)
    return timestamp;
  }
  if (typeof timestamp === 'string') {
    // Attempt to parse string date, though this can be unreliable
    const parsedDate = Date.parse(timestamp);
    if (!isNaN(parsedDate)) {
      return parsedDate;
    }
  }
  console.warn("Failed to convert timestamp to milliseconds. Unrecognized format:", JSON.stringify(timestamp));
  return 0; // Fallback for unrecognized format
};


export const createGreetingMessage = (
  character: Character,
  isIncognito: boolean,
  t: (key: string, replacements?: Record<string, string>) => string
): ChatMessage => ({
  id: getStableGreetingId(character.id, isIncognito),
  role: 'ai',
  content: character.greeting || t('defaultGreeting', { characterName: character.name }),
  timestamp: Date.now(),
  userId: 'ai-greeting',
  isTyping: false,
});


/**
 * Parses a number that may be in a formatted string like "1.8K" or "2.3M" into a number.
 * Handles plain numbers and invalid inputs gracefully.
 * @param value The value to parse, which can be a number, string, or other type.
 * @returns The parsed number, or 0 if parsing is not possible.
 */
export const parseFormattedNumber = (value: any): number => {
  if (typeof value === 'number') {
    return Math.floor(value);
  }
  if (typeof value !== 'string' || !value) {
    return 0;
  }
  const numStr = value.toLowerCase().trim();
  const lastChar = numStr.slice(-1);
  let numPart = parseFloat(numStr);

  if (isNaN(numPart)) {
    return 0;
  }

  if (lastChar === 'k') {
    numPart = numPart * 1000;
  } else if (lastChar === 'm') {
    numPart = numPart * 1000000;
  } else if (lastChar === 'b') {
    numPart = numPart * 1000000000;
  }
  
  return Math.floor(numPart);
};

/**
 * Formats a large number into a more readable string with suffixes (K, M, B).
 * @param num The number to format.
 * @returns A string representation of the number (e.g., "1.8K", "2.3M").
 */
export const formatLargeNumber = (num: number | undefined | null): string => {
  if (num === null || num === undefined || !isFinite(num)) {
    return '0';
  }
  if (num < 1000) {
    return String(num);
  }
  if (num < 1000000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  if (num < 1000000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
};
